/**
 * Run `cb` once the host page's `window.CUIWidgetConfig` exists.
 *
 * Normally the host sets the config in an inline <script> ahead of the embed
 * script, so the config is already there and `cb` runs synchronously. Some
 * page builders (IONOS MyWebsite NOW / go-x "HTML module" blocks) re-create
 * the module's scripts themselves and execute every external <script src>
 * BEFORE the module's inline scripts — the embed script then runs before the
 * config assignment. Booting on an undefined config means no socketUrl (the
 * widget crashed and rendered nothing) or, for the iframe loader, a snapshot
 * without the host's overrides.
 *
 * While waiting, the property is an accessor that fires `cb` on assignment;
 * afterwards it is restored to a plain data property. If no config ever
 * arrives, `cb` runs after the window `load` event (or on the next task when
 * the page has already loaded), so a host page without a config block keeps
 * booting with the variant defaults.
 *
 * Shared by embed-core.tsx and loader/loader.ts — keep it free of React and
 * widget imports; the loader bundle must stay tiny.
 */
export function whenHostConfigReady(win: Window, doc: Document, cb: () => void): void {
    const w = win as Window & { CUIWidgetConfig?: unknown };
    if (w.CUIWidgetConfig !== undefined) {
        cb();
        return;
    }

    let done = false;
    let value: unknown;

    const restore = () => {
        Object.defineProperty(w, 'CUIWidgetConfig', {
            value,
            writable: true,
            configurable: true,
            enumerable: true,
        });
        if (value === undefined) delete w.CUIWidgetConfig;
    };
    const fire = () => {
        if (done) return;
        done = true;
        win.removeEventListener('load', onLoad);
        restore();
        cb();
    };
    // Defer past the current task so inline scripts that run in the same
    // turn as the load event still get to assign the config first.
    const onLoad = () => setTimeout(fire, 0);

    Object.defineProperty(w, 'CUIWidgetConfig', {
        configurable: true,
        enumerable: true,
        get: () => value,
        set: (v: unknown) => {
            value = v;
            fire();
        },
    });

    if (doc.readyState === 'complete') {
        setTimeout(fire, 0);
    } else {
        win.addEventListener('load', onLoad);
    }
}
